/**
 * User Mapper
 * Maps between API DTOs and Domain Entities
 */

import { UserDTO, CreateUserResponseDTO, ListUsersResponseDTO } from "../models/UserDTOs";
import { UserEntity, CompanyAssignmentEntity } from "../../domain/entities/UserEntity";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";

export class UserMapper {
  /**
   * Convert UserDTO to UserEntity
   */
  static toUserEntity(dto: UserDTO): UserEntity {
    // Handle both boolean, string "1", and number 1 for boolean fields
    const isActive = typeof dto.is_active === 'boolean' 
      ? dto.is_active 
      : dto.is_active === 1 || dto.is_active === "1";
    
    const isMasterAdmin = typeof dto.is_master_admin === 'boolean'
      ? dto.is_master_admin
      : dto.is_master_admin === 1 || dto.is_master_admin === "1";
    
    return {
      id: dto.id!,
      email: dto.email,
      userName: dto.userName,
      is_active: isActive,
      is_master_admin: isMasterAdmin,
      password_changed_at: dto.password_changed_at,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      companies: dto.companies?.map((comp) => {
        const isCompanyAdmin = typeof comp.is_company_admin === 'boolean'
          ? comp.is_company_admin
          : comp.is_company_admin === 1 || comp.is_company_admin === "1";
        
        return {
          id: comp.id,
          company_id: comp.id,
          company_name: comp.company_name,
          app_url: comp.app_url,
          role_name: comp.role_name,
          is_company_admin: isCompanyAdmin,
        };
      }),
    };
  }

  /**
   * Convert UserDTO array to UserEntity array
   */
  static toUserEntityArray(dtos: UserDTO[]): UserEntity[] {
    return dtos.map((dto) => this.toUserEntity(dto));
  }

  /**
   * Convert CreateUserResponseDTO to domain result
   */
  static toCreateUserResult(dto: CreateUserResponseDTO): {
    user: UserEntity;
    assigned_companies: CompanyEntity[];
  } {
    return {
      user: this.toUserEntity(dto.data.user),
      assigned_companies: dto.data.assigned_companies.map((comp) => ({
        id: comp.id,
        company_name: comp.company_name,
        app_url: comp.app_url,
      })) as CompanyEntity[],
    };
  }

  /**
   * Convert ListUsersResponseDTO to domain result
   */
  static toListUsersResult(dto: ListUsersResponseDTO): {
    users: UserEntity[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  } {
    return {
      users: this.toUserEntityArray(dto.data.users),
      pagination: dto.data.pagination,
    };
  }
}

